import React, {useEffect, useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  SafeAreaView,
} from 'react-native';
import {
  createAgoraRtcEngine,
  ChannelProfileType,
  ClientRoleType,
  IRtcEngine,
  RtcSurfaceView,
  RenderModeType,
} from 'react-native-agora';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import useStores from '../../stores/useStores';
import {width, height, totalSize} from 'react-native-dimension';
import { colorPalette, colors } from '../../../core/config/color.config';

const APP_ID = '6a66374b48854ec7bc24ba60595af045';

const ConsultationVideoCall = ({navigation, route}: any) => {
  const {token: accessToken, uid, channelName} = route.params;
  const [engine] = useState<IRtcEngine>(() => createAgoraRtcEngine());
  const [joined, setJoined] = useState(false);
  const [remoteUid, setRemoteUid] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [remoteVideoOff, setRemoteVideoOff] = useState(false);
  const {authStore} = useStores();

  const [videoCallToken, setVideoCallToken] = useState('');




  async function requestPermissions() {
    if (Platform.OS === 'android') {
      const PermissionsAndroid = require('react-native').PermissionsAndroid;
      await PermissionsAndroid.requestMultiple([
        PermissionsAndroid.PERMISSIONS.CAMERA,
        PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
      ]);
    }
  }

  useEffect(() => {
    const init = async () => {
      await requestPermissions();

      engine.initialize({
        appId: APP_ID,
        channelProfile: ChannelProfileType.ChannelProfileLiveBroadcasting,
      });

      engine.registerEventHandler({
        onError: (code, msg) => {
          console.log('❌ Agora error:', code, msg);
        },
        onJoinChannelSuccess: () => {
          console.log('✅ Joined Agora channel successfully');
          setJoined(true);
        },
        onUserJoined: (_connection, uid) => {
          console.log('👤 Remote user joined:', uid);
          setRemoteUid(uid);
        },
        onUserOffline: (_connection, uid) => {
          console.log('👋 Remote user left:', uid);
          setRemoteUid(null);
        },
        onUserMuteVideo: (_connection, uid, muted) => {
          console.log('🎥 Remote video state changed:', uid, muted);
          if (muted) {
            setRemoteVideoOff(true);
          } else {
            setRemoteVideoOff(false);
          }
        },
      });

      engine.enableVideo();
      engine.enableAudio();
      engine.muteLocalAudioStream(false);
      engine.setEnableSpeakerphone(true);
      engine.startPreview();
      if (authStore.userRole === 'patient') {
        engine.setClientRole(ClientRoleType.ClientRoleBroadcaster);
      } else {
        engine.setClientRole(ClientRoleType.ClientRoleBroadcaster);
      }
      // engine.joinChannel(TOKEN, CHANNEL_NAME, 0);
      engine.joinChannel(accessToken, channelName, uid, {
        clientRoleType: ClientRoleType.ClientRoleBroadcaster,
        channelProfile: ChannelProfileType.ChannelProfileLiveBroadcasting,
        publishCameraTrack: true,
        publishMicrophoneTrack: true,
      });
    };

    init();

    return () => {
      engine.leaveChannel();
      engine.release();
    };
  }, [engine]);

  const toggleMute = () => {
    const newState = !isMuted;
    setIsMuted(newState);
    engine.muteLocalAudioStream(newState);
  };

  const toggleCamera = () => {
    const newState = !isCameraOn;
    setIsCameraOn(newState);
    engine.enableLocalVideo(newState);
    if (newState) {
      engine.startPreview();
    } else {
      engine.stopPreview();
    }
  };

  const switchCamera = () => {
    engine.switchCamera();
    setIsFrontCamera(!isFrontCamera);
  };

  return (
    <View style={styles.container}>
      {joined ? (
        <>
          {/* Remote Video */}
          {remoteUid ? (
            remoteVideoOff ? (
              <View style={[styles.remoteVideo, styles.placeholder]}>
                <Icon name="account" size={100} color="#999" />
                {/* <Text style={styles.placeholderText}>Video Off</Text> */}
              </View>
            ) : (
              <RtcSurfaceView
                style={styles.remoteVideo}
                canvas={{uid: remoteUid}}
              />
            )
          ) : // <RtcSurfaceView style={styles.remoteVideo} canvas={{uid: remoteUid}} />

          authStore?.userRole === 'patient' ? (
            <Text style={styles.waitingText}>
              Waiting for doctor to join...
            </Text>
          ) : (
            <Text style={styles.waitingText}>
              Waiting for patient to join...
            </Text>
          )}

          {/* Local Video (Self View) */}
          {isCameraOn ? (
            <RtcSurfaceView
              style={[styles.localVideo,{borderWidth: Platform.OS === 'android' ? totalSize(0) : totalSize(0),borderColor: colorPalette.secondary,}]}
              canvas={{uid: 0, renderMode: RenderModeType.RenderModeFit}}
              zOrderMediaOverlay={true}
            />
          ) : (
            <View style={[styles.localVideo, styles.placeholder, {borderWidth: 1,borderColor: colors.gray.darker,}]}>
              <Icon name="account" size={50} color="#999" />
              {/* <Text style={styles.placeholderText}>Camera Off</Text> */}
            </View>
          )}

          {/* Bottom Controls */}
          <View style={styles.controls}>
            {/* Mute / Unmute */}
            <TouchableOpacity style={styles.controlButton} onPress={toggleMute}>
              <Icon
                name={isMuted ? 'microphone-off' : 'microphone'}
                size={28}
                color="#fff"
              />
            </TouchableOpacity>

            {/* Switch Camera */}
            <TouchableOpacity
              style={styles.controlButton}
              onPress={switchCamera}>
              <Icon name="camera-switch" size={28} color="#fff" />
            </TouchableOpacity>

            {/* End Call */}
            <TouchableOpacity
              style={[styles.controlButton, {backgroundColor: 'red'}]}
              onPress={() => {
                engine.leaveChannel();
                navigation.goBack();
              }}>
              <Icon name="phone-hangup" size={28} color="#fff" />
            </TouchableOpacity>

            {/* Toggle Video */}
            <TouchableOpacity
              style={styles.controlButton}
              onPress={toggleCamera}>
              <Icon
                name={isCameraOn ? 'video' : 'video-off'}
                size={28}
                color="#fff"
              />
            </TouchableOpacity>
          </View>
        </>
      ) : (
        <Text style={styles.waitingText}>Joining...</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: '#000'},
  remoteVideo: {flex: 1},
  localVideo: {
    position: 'absolute',
    bottom: 110,
    right: 10,
    width: '30%',
    height: '30%',
    borderRadius: 10,
    overflow: 'hidden',
    
  },
  waitingText: {
    color: '#fff',
    textAlign: 'center',
    marginTop: Platform.OS === 'android' ? 20 : totalSize(8),
    fontFamily: 'Quicksand-SemiBold',
    fontSize: 18,
  },
  controls: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 25,
  },
  controlButton: {
    backgroundColor: '#333',
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#111',
  },
  placeholderText: {
    color: '#999',
    marginTop: 10,
    fontSize: 16,
    fontFamily: 'Quicksand-SemiBold',
  },
});

export default ConsultationVideoCall;
